import { useState } from "react";
import { ChatStore } from "@/stores/ChatStore";
import { motion, AnimatePresence } from "framer-motion";
import { CirclePlay } from "lucide-react";
import SettingsStore from "@/stores/SettingsStore";
import { fetchSessionId } from "@/services/schaden-ki/fetch_session_id";

export default function PlayLayer() {
  const [isHovered, setIsHovered] = useState(false);

  const isRunning = ChatStore(state => state.isRunning);
  const setIsRunning = ChatStore(state => state.setIsRunning);
  const setIsHistoryPanelOpen = ChatStore(state => state.setIsHistoryPanelOpen);
  const resetMessages = ChatStore(state => state.resetMessages);
  const displayHistory = SettingsStore(state => state.displayHistory);
  const isAvatarLoading = SettingsStore(state => state.isLoading);
  const brandingPlayLogoUrl = SettingsStore(state => state.brandingPlayLogoUrl);
  const brandingPlayLogoHoverUrl = SettingsStore(state => state.brandingPlayLogoHoverUrl);
  const useSchadenKI = SettingsStore(state => state.useSchadenKI);

  if (isRunning || isAvatarLoading) return null;

  const start = () => {
    resetMessages();
    if (useSchadenKI) {
      fetchSessionId()
        .catch(err => {
          console.error("Error fetching session ID:", err);
        });
    }
    setIsRunning(true);
    setIsHistoryPanelOpen(displayHistory);
  };

  const hasBrandedImage = !!brandingPlayLogoUrl;
  const hasHoverImage = !!brandingPlayLogoHoverUrl;

  return (
    <motion.div
      className="absolute inset-0 flex w-full h-full cursor-pointer items-center justify-center"
      animate={{ opacity: 1, scale: 1, backgroundColor: "rgba(255,255,255,0)" }}
      whileHover={{ backgroundColor: "rgba(255,255,255,0.5)" }}
      transition={{ duration: 0.2 }}
      onClick={start}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <motion.button
        className="flex items-center justify-center w-1/4 aspect-square rounded-full bg-white shadow-lg transition-colors duration-200 ease-in-out focus:outline-none"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.55)" }}
        aria-label="Play"
        animate={{ scale: isHovered ? 1.1 : 1 }}
        transition={{ duration: 0.2 }}
        autoFocus
      >
        <div className="w-3/4 aspect-square flex items-center justify-center relative">
          {hasBrandedImage ? (
            hasHoverImage ? (
              <AnimatePresence mode="wait">
                <motion.img
                  key={isHovered ? "hover" : "default"}
                  src={isHovered ? brandingPlayLogoHoverUrl : brandingPlayLogoUrl}
                  alt="Start"
                  className="h-full w-full rounded-full absolute inset-0"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                />
              </AnimatePresence>
            ) : (
              <motion.img
                src={brandingPlayLogoUrl}
                alt="Start"
                className="h-full w-full rounded-full"
                initial={false}
                animate={{ rotate: isHovered ? -28 : 0 }}
                transition={{ duration: 0.2 }}
              />
            )
          ) : (
            <motion.div
              initial={false}
              animate={{ rotate: isHovered ? -28 : 0 }}
              transition={{ duration: 0.2 }}
              className="h-full w-full flex items-center justify-center"
            >
              <CirclePlay className="h-full w-full text-gray-500" strokeWidth={1} />
            </motion.div>
          )}
        </div>
      </motion.button>
    </motion.div>
  );
}
